import "@fontsource-variable/bricolage-grotesque";
import "@fontsource-variable/hanken-grotesk";
import "./style.css";

import { resolveAllergen, MenuSchema, type Category, type Dish, type Menu, type Theme, type Tier } from "@arfood/shared";
import { createDishCardT1, createModelViewer, type ModelViewerElement } from "./render/t1.ts";
import { createDishCardT0 } from "./render/t0.ts";
import { el, formatPrice, pickI18n } from "./render/util.ts";
import { readContext, type AppContext } from "./context.ts";
import { count, flush, initAnalytics } from "./analytics.ts";
import { createOrder, type OrderController } from "./order.ts";

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
const DEFAULT_VENUE = import.meta.env.VITE_VENUE ?? "demo";
const ID_RE = /^[\w.:-]{1,64}$/;

function venueFromUrl(): string {
  const v = new URLSearchParams(location.search).get("v");
  return v && ID_RE.test(v) ? v : DEFAULT_VENUE;
}

async function loadMenu(venue: string): Promise<Menu> {
  if (!API_BASE) throw new Error("VITE_API_BASE_URL nicht gesetzt");
  const res = await fetch(`${API_BASE}/menu/${encodeURIComponent(venue)}`);
  if (!res.ok) throw new Error(`Menü "${venue}": HTTP ${res.status}`);
  return MenuSchema.parse(await res.json());
}

// --- Modulzustand (im Bootstrap gesetzt) ---
let menu: Menu;
let context: AppContext;
let pageTier: Tier;
let order: OrderController;
const state = { lang: "de" };

const dishTier = new Map<string, Tier>();
const tierOf = (id: string): Tier => dishTier.get(id) ?? pageTier;

const viewed = new Set<string>();
const viewObserver = new IntersectionObserver(
  (entries) => {
    for (const e of entries) {
      const id = (e.target as HTMLElement).dataset["dish"];
      if (!e.isIntersecting || !id || viewed.has(id)) continue;
      viewed.add(id);
      count.view(id);
      viewObserver.unobserve(e.target);
    }
  },
  { threshold: 0.5 },
);
function observeView(card: HTMLElement, id: string): void {
  if (!viewed.has(id)) viewObserver.observe(card);
}

// --- Theme pro Betrieb → CSS-Variablen (Werte sind hex-validiert, kein Injection-Vektor) ---
function applyTheme(theme: Theme | undefined): void {
  if (!theme) return;
  const root = document.documentElement;
  const set = (k: string, v: string | undefined): void => {
    if (v) root.style.setProperty(k, v);
  };
  set("--bg", theme.bg);
  set("--card", theme.card);
  set("--fg", theme.fg);
  set("--muted", theme.muted);
  set("--accent", theme.accent);
  set("--aink", theme.aink);
  set("--border", theme.border);
  set("--media-bg", theme.mediaBg);
  if (theme.colorScheme) root.style.colorScheme = theme.colorScheme;
  if (theme.themeColor) document.querySelector('meta[name="theme-color"]')?.setAttribute("content", theme.themeColor);
}

// --- Kategorien ---
interface Group {
  category?: Category;
  dishes: Dish[];
}
function groupDishes(): Group[] {
  const cats = [...menu.categories].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const known = new Set(cats.map((c) => c.id));
  const byCat = new Map<string, Dish[]>();
  const uncategorized: Dish[] = [];
  for (const d of menu.dishes) {
    if (d.category && known.has(d.category)) {
      let arr = byCat.get(d.category);
      if (!arr) byCat.set(d.category, (arr = []));
      arr.push(d);
    } else {
      uncategorized.push(d);
    }
  }
  const sort = (arr: Dish[]): Dish[] => arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const groups: Group[] = [];
  for (const c of cats) {
    const ds = byCat.get(c.id);
    if (ds?.length) groups.push({ category: c, dishes: sort(ds) });
  }
  if (uncategorized.length) groups.push({ dishes: sort(uncategorized) });
  return groups;
}
function categoryLabel(c: Category): string {
  const byLang = c.i18n?.[state.lang]?.name;
  const firstI18n = c.i18n ? Object.values(c.i18n)[0]?.name : undefined;
  return byLang ?? firstI18n ?? c.label ?? c.id;
}

function initialLang(): string {
  if (context.lang && menu.languages.includes(context.lang)) return context.lang;
  return menu.languages[0] ?? "de";
}

function relabel(): void {
  const grid = document.getElementById("menu-grid");
  if (grid) {
    for (const card of grid.querySelectorAll<HTMLElement>("[data-dish]")) {
      const dish = menu.dishes.find((d) => d.id === card.dataset["dish"]);
      if (!dish) continue;
      const { name, desc } = pickI18n(dish, state.lang);
      const nameEl = card.querySelector(".dish-name");
      if (nameEl) nameEl.textContent = name;
      const descEl = card.querySelector(".dish-desc");
      if (descEl) descEl.textContent = desc;
      card.querySelector("model-viewer")?.setAttribute("alt", name);
    }
    for (const h of grid.querySelectorAll<HTMLElement>(".category-heading")) {
      const cat = menu.categories.find((c) => c.id === h.dataset["cat"]);
      if (cat) h.textContent = categoryLabel(cat);
    }
  }
  for (const chip of document.querySelectorAll<HTMLElement>("#cat-chips .cat-chip")) {
    const cat = menu.categories.find((c) => c.id === chip.dataset["cat"]);
    if (cat) chip.textContent = categoryLabel(cat);
  }
}

function setLanguage(lang: string): void {
  if (state.lang === lang) return;
  state.lang = lang;
  for (const btn of document.querySelectorAll<HTMLElement>(".lang-btn")) {
    btn.setAttribute("aria-pressed", String(btn.textContent === lang.toUpperCase()));
  }
  relabel();
}

function buildLangSwitch(): void {
  const nav = document.getElementById("lang-switch");
  if (!nav) return;
  nav.replaceChildren();
  if (menu.languages.length < 2) return;
  for (const lang of menu.languages) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "lang-btn";
    btn.textContent = lang.toUpperCase();
    btn.setAttribute("aria-pressed", String(lang === state.lang));
    btn.addEventListener("click", () => setLanguage(lang));
    nav.appendChild(btn);
  }
}

function buildHeader(): void {
  const title = document.getElementById("venue-title");
  if (title) title.textContent = menu.venueId;
  const logo = document.getElementById("venue-logo");
  if (logo) logo.textContent = (menu.venueId[0] ?? "F").toUpperCase();
  const sub = document.getElementById("venue-sub");
  if (sub) sub.textContent = context.table ? `Tisch ${context.table} · Speisekarte` : "Speisekarte";
}

function buildChips(groups: Group[]): void {
  const host = document.getElementById("cat-chips");
  if (!host) return;
  host.replaceChildren();
  const withCat = groups.filter((g) => g.category);
  if (withCat.length < 2) return; // Chips nur sinnvoll ab 2 Kategorien
  withCat.forEach((g, i) => {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "cat-chip" + (i === 0 ? " cat-chip--active" : "");
    chip.dataset["cat"] = g.category!.id;
    chip.textContent = categoryLabel(g.category!);
    chip.addEventListener("click", () => {
      for (const c of host.querySelectorAll(".cat-chip")) c.classList.remove("cat-chip--active");
      chip.classList.add("cat-chip--active");
      document.querySelector(`.category-heading[data-cat="${g.category!.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    host.appendChild(chip);
  });
}

// --- AR-Tracking (Karte + Detail teilen sich das) ---
function wireArStatus(mv: ModelViewerElement, dishId: string, tier: Tier): void {
  let arEnteredAt = 0;
  mv.addEventListener("ar-status", (ev) => {
    const status = (ev as CustomEvent<{ status: string }>).detail.status;
    if (status === "session-started") {
      arEnteredAt = Date.now();
      count.arEnter(dishId);
    } else if (status === "not-presenting" && arEnteredAt > 0) {
      count.arDwell(dishId, Date.now() - arEnteredAt);
      arEnteredAt = 0;
    }
  });
}

function wireModelViewer(dish: Dish, card: HTMLElement, mv: ModelViewerElement): void {
  mv.addEventListener(
    "error",
    () => {
      viewObserver.unobserve(card);
      const fallback = createDishCardT0(dish, state.lang);
      dishTier.set(dish.id, "T0");
      card.replaceWith(fallback);
      count.fallback();
      observeView(fallback, dish.id);
    },
    { once: true },
  );
  mv.addEventListener(
    "load",
    () => {
      if (mv.canActivateAR === false) count.arUnsupported();
    },
    { once: true },
  );
  wireArStatus(mv, dish.id, "T1");
}

// Ein Gericht rendern: T0 wenn erzwungen, kein Modell, Modell nicht fertig oder als
// Kontrollvariante des AR-Experiments ausgeblendet; sonst T1.
// Wichtig: `fallback_shown` feuert nur bei erzwungenem T0 — die Kontrollvariante darf die
// Fallback-Rate nicht verfälschen, die Geräte-Unfähigkeit misst, keine bewusste Ausblendung.
function appendDishCard(parent: HTMLElement, dish: Dish): void {
  const modelReady =
    dish.modelStatus !== "processing" && dish.modelStatus !== "failed" && !!dish.model.glb && dish.arHidden !== true;
  if (context.tier === "T0" || !modelReady) {
    const card = createDishCardT0(dish, state.lang);
    dishTier.set(dish.id, "T0");
    parent.appendChild(card);
    if (context.tier === "T0") count.fallback();
    observeView(card, dish.id);
    return;
  }
  if (context.tier === "T2") console.info("[arfood] T2 (8th Wall) noch nicht implementiert — nutze T1.");
  const { card, modelViewer } = createDishCardT1(dish, state.lang);
  dishTier.set(dish.id, "T1");
  parent.appendChild(card);
  observeView(card, dish.id);
  wireModelViewer(dish, card, modelViewer);
}

function buildGrid(): void {
  const grid = document.getElementById("menu-grid");
  if (!grid) return;
  grid.replaceChildren();
  const groups = groupDishes();
  if (groups.length === 0) {
    showState("🍽️", "Menü in Arbeit", "Die Karte wird gerade eingerichtet.");
    return;
  }
  for (const group of groups) {
    if (group.category) {
      const h = el("h2", "category-heading");
      h.dataset["cat"] = group.category.id;
      h.textContent = categoryLabel(group.category);
      grid.appendChild(h);
    }
    const section = el("div", "menu-section");
    for (const dish of group.dishes) appendDishCard(section, dish);
    grid.appendChild(section);
  }
  buildChips(groups);
}

// --- Detail-Overlay (A4) ---
function openDetail(dish: Dish): void {
  const host = document.getElementById("dish-detail");
  if (!host) return;
  const { name, desc } = pickI18n(dish, state.lang);
  const entry = dish.i18n[state.lang] ?? Object.values(dish.i18n)[0];
  const tier = tierOf(dish.id);
  const hasModel = tier !== "T0" && !!dish.model.glb && dish.modelStatus !== "processing" && dish.modelStatus !== "failed";

  const panel = el("div", "detail-panel");

  const hero = el("div", "detail-hero");
  let heroMv: ModelViewerElement | null = null;
  if (hasModel) {
    heroMv = createModelViewer(dish, name);
    wireArStatus(heroMv, dish.id, tier);
    hero.appendChild(heroMv);
    const badge = el("span", "badge-3d");
    badge.textContent = "◆ 3D";
    hero.appendChild(badge);
  } else if (dish.model.poster) {
    const img = document.createElement("img");
    img.className = "dish-media";
    img.src = dish.model.poster;
    img.alt = name;
    hero.appendChild(img);
    hero.appendChild(labelBadge("Foto"));
  } else {
    hero.classList.add("dish-media--placeholder");
  }
  const back = document.createElement("button");
  back.type = "button";
  back.className = "detail-back";
  back.dataset["detail"] = "close";
  back.setAttribute("aria-label", "Zurück");
  back.textContent = "‹";
  hero.appendChild(back);
  panel.appendChild(hero);

  const body = el("div", "detail-body");
  const head = el("div", "dish-head");
  const h = el("h2", "detail-name");
  h.textContent = name;
  const price = el("span", "dish-price");
  price.textContent = formatPrice(dish.priceCents);
  head.append(h, price);
  body.appendChild(head);

  if (desc) {
    const p = el("p", "detail-desc");
    p.textContent = desc;
    body.appendChild(p);
  }
  if (dish.allergens.length) {
    body.appendChild(sectionLabel("Allergene"));
    const list = el("div", "detail-allergens");
    for (const a of dish.allergens) {
      const chip = el("span", "allergen-chip");
      chip.textContent = resolveAllergen(a, state.lang).full;
      list.appendChild(chip);
    }
    body.appendChild(list);
  }
  if (entry?.ingredients) {
    body.appendChild(sectionLabel("Zutaten"));
    const p = el("p", "detail-ing");
    p.textContent = entry.ingredients;
    body.appendChild(p);
  }

  const actions = el("div", "detail-actions");
  if (hasModel) {
    const ar = document.createElement("button");
    ar.type = "button";
    ar.className = "btn-primary detail-btn";
    ar.dataset["detail"] = "ar";
    ar.textContent = "In AR ansehen";
    actions.appendChild(ar);
  }
  const add = document.createElement("button");
  add.type = "button";
  add.className = "btn-secondary detail-btn";
  add.dataset["detail"] = "order";
  add.textContent = "+ Hinzufügen";
  actions.appendChild(add);
  body.appendChild(actions);

  panel.appendChild(body);
  panel.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-detail]");
    const act = btn?.dataset["detail"];
    if (act === "close") closeDetail();
    else if (act === "ar" && btn) triggerAr(btn, heroMv);
    else if (act === "order" && btn) addToOrder(dish.id, btn);
  });

  host.replaceChildren(panel);
  host.hidden = false;
  document.body.classList.add("detail-open");
  count.detailOpen(dish.id);
}
function closeDetail(): void {
  const host = document.getElementById("dish-detail");
  if (host) {
    host.hidden = true;
    host.replaceChildren();
  }
  document.body.classList.remove("detail-open");
}
function labelBadge(text: string): HTMLElement {
  const b = el("span", "badge-media");
  b.textContent = text;
  return b;
}
function sectionLabel(text: string): HTMLElement {
  const s = el("div", "detail-section-label");
  s.textContent = text;
  return s;
}

// --- Interaktions-Feedback ---
function flashLabel(button: HTMLElement, text: string, ms: number): void {
  if (button.dataset["flashing"]) return;
  button.dataset["flashing"] = "1";
  const orig = button.textContent;
  button.textContent = text;
  window.setTimeout(() => {
    button.textContent = orig;
    delete button.dataset["flashing"];
  }, ms);
}
function bumpOrderBar(): void {
  const inner = document.querySelector<HTMLElement>(".order-bar-inner");
  if (!inner) return;
  inner.classList.remove("bump");
  void inner.offsetWidth; // Reflow → Animation neu starten
  inner.classList.add("bump");
}
function flashAdded(button: HTMLElement): void {
  button.classList.add("added");
  window.setTimeout(() => button.classList.remove("added"), 400);
  if (button.classList.contains("btn-add")) flashLabel(button, "✓", 600);
  else flashLabel(button, "✓ Hinzugefügt", 900);
}
function addToOrder(id: string, button: HTMLElement): void {
  order.add(id);
  bumpOrderBar();
  flashAdded(button);
}
// „In AR ansehen": Spinner bis die AR-Session startet (Modell/Quick-Look-Ladezeit); Timeout/Abbruch räumt auf.
function triggerAr(button: HTMLElement, mv: ModelViewerElement | null): void {
  if (!mv || button.classList.contains("is-loading")) return;
  if (mv.canActivateAR === false) {
    flashLabel(button, "AR nicht verfügbar", 1600);
    return;
  }
  button.classList.add("is-loading");
  const done = (): void => {
    button.classList.remove("is-loading");
    window.clearTimeout(timer);
    mv.removeEventListener("ar-status", done);
  };
  mv.addEventListener("ar-status", done, { once: true });
  const timer = window.setTimeout(done, 8000);
  try {
    mv.activateAR?.();
  } catch {
    done();
  }
}

function onGridClick(e: MouseEvent): void {
  const trigger = (e.target as HTMLElement).closest<HTMLElement>("[data-action]");
  const card = trigger?.closest<HTMLElement>("[data-dish]");
  const id = card?.dataset["dish"];
  if (!trigger || !id) return;
  const action = trigger.dataset["action"];
  if (action === "order") {
    addToOrder(id, trigger);
  } else if (action === "ar") {
    triggerAr(trigger, card?.querySelector<ModelViewerElement>("model-viewer") ?? null);
  } else if (action === "details") {
    const dish = menu.dishes.find((d) => d.id === id);
    if (dish) openDetail(dish);
  }
}

function showState(icon: string, title: string, text: string): void {
  const grid = document.getElementById("menu-grid");
  if (!grid) return;
  const box = el("div", "state-box");
  const i = el("div", "state-icon");
  i.textContent = icon;
  const h = el("div", "state-title");
  h.textContent = title;
  const p = el("p", "state-text");
  p.textContent = text;
  box.append(i, h, p);
  grid.replaceChildren(box);
}

function showSkeleton(): void {
  const grid = document.getElementById("menu-grid");
  if (!grid) return;
  const section = el("div", "menu-section");
  for (let i = 0; i < 4; i++) {
    const sk = el("div", "skeleton-card");
    sk.append(el("div", "skeleton-media"), el("div", "skeleton-line"), el("div", "skeleton-line short"));
    section.appendChild(sk);
  }
  grid.replaceChildren(section);
}

function renderTextFallback(): void {
  const grid = document.getElementById("menu-grid");
  if (!grid) return;
  grid.replaceChildren();
  const section = el("div", "menu-section");
  for (const dish of menu.dishes) {
    dishTier.set(dish.id, "T0");
    section.appendChild(createDishCardT0(dish, state.lang));
  }
  grid.appendChild(section);
}

// --- Session-Verweildauer: RAM-only, ein Beacon bei erstem Verlassen (cookiefrei) ---
// Bestellung an den Betrieb übergeben. Die Tischnummer stammt aus dem QR-Kontext und
// gehört genau hierher (Art. 6 Abs. 1 lit. b) — sie fließt NICHT in die Nutzungsstatistik.
// Ist kein Kassensystem angebunden, speichert der Server nichts und antwortet
// stored:false; die Merkliste bleibt dann der Weg „dem Personal zeigen".
async function submitOrder(items: { dish: string; qty: number }[]): Promise<void> {
  if (!API_BASE || items.length === 0) return;
  try {
    const res = await fetch(`${API_BASE}/order`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        venue: context.venue,
        ...(context.loc ? { loc: context.loc } : {}),
        ...(context.table ? { table: context.table } : {}),
        items,
        status: "intent",
        ts: Date.now(),
      }),
      keepalive: true,
    });
    if (!res.ok) return;
    const body = (await res.json().catch(() => null)) as { forwarded?: boolean } | null;
    const status = document.getElementById("order-status");
    if (body?.forwarded && status) {
      status.textContent = context.table
        ? `Bestellung an Tisch ${context.table} übermittelt.`
        : "Bestellung übermittelt.";
    }
  } catch {
    // Stumm: die Merkliste am Tisch funktioniert unabhängig davon weiter.
  }
}

// Ein einziges Aggregat pro Besuch: beim ersten Verlassen der Seite (oder schon bei der
// Bestellabsicht) gesendet. flush() ist idempotent.
async function bootstrap(): Promise<void> {
  showSkeleton();
  const venue = venueFromUrl();
  try {
    menu = await loadMenu(venue);
  } catch (err) {
    console.error("[arfood] Menü konnte nicht geladen werden.", err);
    showState("🔍", "Menü nicht gefunden", "QR-Code erneut scannen oder das Personal fragen.");
    return;
  }

  context = readContext(menu.venueId);
  pageTier = context.tier ?? "T1";
  state.lang = initialLang();
  applyTheme(menu.theme);

  initAnalytics({
    venue: context.venue,
    loc: context.loc,
    device: context.device,
    getLang: () => state.lang,
    getTier: () => pageTier,
  });
  order = createOrder(
    menu,
    () => state.lang,
    (items) => {
      count.intent(items); // Statistik: nur Mengen, kein Tisch
      void submitOrder(items); // Bestellfluss: mit Tisch, nur bei angebundenem POS gespeichert
    },
    (change, dishId) => (change === "add" ? count.cartAdd(dishId) : count.cartRemove(dishId)),
  );

  buildHeader();
  document.getElementById("menu-grid")?.addEventListener("click", onGridClick);
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeDetail();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
  window.addEventListener("pagehide", flush);

  try {
    buildLangSwitch();
    buildGrid();
  } catch (err) {
    console.error("[arfood] Render fehlgeschlagen — Text-Fallback.", err);
    renderTextFallback();
  }
}

// model-viewer parallel vorwärmen (Progressive Enhancement); darf den Bootstrap nicht blockieren.
void import("@google/model-viewer").catch((err: unknown) => {
  console.error("[arfood] model-viewer konnte nicht laden — Menü läuft ohne 3D/AR weiter.", err);
});

void bootstrap();
